import {
  AlertTriangle,
  Bug,
  Flower2,
  Info,
  Leaf,
  Ruler,
  ShieldCheck,
  Snowflake,
  Sprout,
  Sun,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import PlantDetailSection from './PlantDetailSection';
import ToxicWarningBadge from './ToxicWarningBadge';
import HardinessWarningBadge from './HardinessWarningBadge';
import DataDisclaimerBadge from '../../common/DataDisclaimerBadge';
import { useGardenSettings } from '../../../context/GardenSettingsContext';
import { getPlantImageUrl } from '../../../utils/plantImageUtils';
import { sanitizePlantNote } from '../../../utils/plantNotes';
import { FLOWER_COLOR_MAP } from '../../../constants/flowerColors';
import {
  SUNLIGHT_LABELS_DETAILED,
  WATER_REQUIREMENT_LABELS_DETAILED,
  SOIL_TYPE_LABELS,
  PLANT_TYPE_LABELS,
  PRUNING_TYPE_LABELS,
  translateEnum,
  translateEnumList,
} from '../../../utils/plantEnumLabels';

/**
 * PlantDetailDialog - Comprehensive plant information modal (1188×750px)
 *
 * Displays detailed plant information across 11 sections (including conditional Kletterpflanzen)
 * with 512×512px image. Supports v7 database schema with 45+ fields displayed.
 *
 * Sections:
 * 1. Grundinformationen
 * 2. Standort & Boden (with pH range parsing)
 * 3. Wuchseigenschaften (space requirement, root type, activity season)
 * 4. Blüte & Ästhetik (with scent intensity/type)
 * 5. Sicherheit & Giftigkeit (with thorns warning)
 * 6. Winterhärte (frost protection, hardiness zones)
 * 7. Pflege (care priority, watering, pruning, fertilizing, mulching, repotting, propagation)
 * 8. Kletterpflanzen (conditional - only shown for climbers)
 * 9. Ökologie & Besonderheiten (bee-friendly as enum)
 * 10. Begleitpflanzen & Verwendung
 * 11. Zusätzliche Hinweise
 *
 * Features:
 * - Large square plant image (512×512px for AI-generated images)
 * - 11 scrollable content sections (8-11 depending on plant type)
 * - Toxic warning/safe badges + thorns warning
 * - Conditional climbing plants section with masonry damage warning
 * - Care priority badges (low/medium/high)
 * - Focus trap and keyboard navigation
 * - ESC key and overlay click to close
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether dialog is open
 * @param {Function} props.onClose - Callback to close dialog
 * @param {Object} props.plant - Plant object with full properties (v7 schema)
 */
const PlantDetailDialog = ({ isOpen, onClose, plant }) => {
  const dialogRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const { settings } = useGardenSettings();

  // Handle the fade-in: render once at the "from" state, then flip on the next
  // frame so the CSS transition runs. Reset on close (cleanup) for the next open.
  useEffect(() => {
    if (!isOpen) return;
    const raf = requestAnimationFrame(() => setIsVisible(true));
    return () => {
      cancelAnimationFrame(raf);
      setIsVisible(false);
    };
  }, [isOpen]);

  // Handle overlay click
  const handleOverlayClick = e => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle ESC key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Focus trap
  useEffect(() => {
    if (!isOpen || !dialogRef.current) return;

    const dialog = dialogRef.current;
    const focusableElements = dialog.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = e => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    dialog.addEventListener('keydown', handleTabKey);
    firstElement.focus();

    return () => dialog.removeEventListener('keydown', handleTabKey);
  }, [isOpen]);

  if (!isOpen) return null;
  if (!plant) return null;

  // Extract plant properties (support both flat and nested formats)
  const {
    // id, // Not used in component
    name,
    german_name,
    scientific_name,
    properties = {},
    care_instructions = {},
    // Flat properties (API list view)
    sunlight: flatSunlight,
    water_requirement: flatWaterRequirement,
    min_height_cm: flatHeightMin,
    max_height_cm: flatHeightMax,
    min_width_cm: flatWidthMin,
    max_width_cm: flatWidthMax,
    toxic_to_pets: flatToxicToPets,
    toxic_to_children: flatToxicToChildren,
    toxic_to_cats: flatToxicToCats,
    toxic_to_dogs: flatToxicToDogs,
    toxicity_level: flatToxicityLevel,
    evergreen: flatEvergreen,
    bee_friendly: flatBeeFriendly,
    frost_hardy: flatFrostHardy,
    drought_tolerant: flatDroughtTolerant,
    suitable_for_pot: flatSuitableForPot,
    suitable_for_planter: flatSuitableForPlanter,
    flower_color: flatFlowerColor,
    flowering_months: flatFloweringMonths,
    growth_speed: flatGrowthSpeed,
    soil_type: flatSoilType,
    ph_min: flatPhMin,
    ph_max: flatPhMax,
    hardiness_zone: flatHardinessZone,
    native_region: flatNativeRegion,
    deer_resistant: flatDeerResistant,
    family: flatFamily,
    plant_type: flatPlantType,
  } = plant;

  // Support both flat and nested property formats
  const sunlight = flatSunlight || properties.sunlight;
  const water_requirement =
    flatWaterRequirement || properties.water_requirement;
  const height_min =
    flatHeightMin !== undefined ? flatHeightMin : properties.min_height_cm;
  const height_max =
    flatHeightMax !== undefined ? flatHeightMax : properties.max_height_cm;
  const width_min =
    flatWidthMin !== undefined ? flatWidthMin : properties.min_width_cm;
  const width_max =
    flatWidthMax !== undefined ? flatWidthMax : properties.max_width_cm;
  const toxic_to_pets =
    flatToxicToPets !== undefined ? flatToxicToPets : properties.toxic_to_pets;
  const toxic_to_children =
    flatToxicToChildren !== undefined
      ? flatToxicToChildren
      : properties.toxic_to_children;
  const toxic_to_cats =
    flatToxicToCats !== undefined ? flatToxicToCats : properties.toxic_to_cats;
  const toxic_to_dogs =
    flatToxicToDogs !== undefined ? flatToxicToDogs : properties.toxic_to_dogs;
  const toxicity_level =
    properties.toxicity_level || flatToxicityLevel || 'none';
  const evergreen =
    flatEvergreen !== undefined ? flatEvergreen : properties.evergreen;
  const bee_friendly =
    flatBeeFriendly !== undefined ? flatBeeFriendly : properties.bee_friendly;
  const frost_hardy =
    flatFrostHardy !== undefined ? flatFrostHardy : properties.frost_hardy;
  const drought_tolerant =
    flatDroughtTolerant !== undefined
      ? flatDroughtTolerant
      : properties.drought_tolerant;
  const suitable_for_pot =
    flatSuitableForPot !== undefined
      ? flatSuitableForPot
      : properties.suitable_for_pot;
  const suitable_for_planter =
    flatSuitableForPlanter !== undefined
      ? flatSuitableForPlanter
      : properties.suitable_for_planter;
  const flower_color = flatFlowerColor || properties.flower_color;
  const flowering_months = flatFloweringMonths || properties.flowering_months;
  const growth_speed = flatGrowthSpeed || properties.growth_speed;
  const soil_type = flatSoilType || properties.soil_type;
  const ph_min = flatPhMin !== undefined ? flatPhMin : properties.ph_min;
  const ph_max = flatPhMax !== undefined ? flatPhMax : properties.ph_max;
  const hardiness_zone = flatHardinessZone || properties.hardiness_zone;
  const native_region = flatNativeRegion || properties.native_region;
  const deer_resistant =
    flatDeerResistant !== undefined
      ? flatDeerResistant
      : properties.deer_resistant;
  const family = flatFamily || properties.family;
  const plant_type = flatPlantType || properties.plant_type;

  // Extract new v7 fields (from properties JSONB)
  const space_requirement_cm = properties.space_requirement_cm;
  const growth_rate_cm_per_year = properties.growth_rate_cm_per_year;
  const root_type = properties.root_type;
  const season_start_month = properties.season_start_month;
  const season_end_month = properties.season_end_month;
  const scent_intensity = properties.scent_intensity;
  const scent_type = properties.scent_type;
  const has_thorns = properties.has_thorns;
  const ph_range = properties.ph_range;
  const is_climber = properties.is_climber;
  const climbing_type = properties.climbing_type;
  const needs_climbing_support = properties.needs_climbing_support;
  const invasive_to_masonry = properties.invasive_to_masonry;
  const growth_type = properties.growth_type;

  // Prefer german_name if available, fallback to name
  const displayName = german_name || name;
  const imageName = name || german_name; // DB 'name' matches image filename

  // Helper functions for display formatting (defined BEFORE fallback generators)
  const getSunlightDisplay = value =>
    translateEnumList(SUNLIGHT_LABELS_DETAILED, value, 'sunlight') || 'Unbekannt';

  const getWaterDisplay = value =>
    translateEnum(WATER_REQUIREMENT_LABELS_DETAILED, value, 'water_requirement') || 'Unbekannt';

  const getHeightDisplay = () => {
    if (height_min && height_max) {
      return `${height_min} - ${height_max} cm`;
    } else if (height_min) {
      return `ab ${height_min} cm`;
    } else if (height_max) {
      return `bis ${height_max} cm`;
    }
    return growth_type === 'lawn' ? '–' : 'Unbekannt';
  };

  const getWidthDisplay = () => {
    if (width_min && width_max) {
      return `${width_min} - ${width_max} cm`;
    } else if (width_min) {
      return `ab ${width_min} cm`;
    } else if (width_max) {
      return `bis ${width_max} cm`;
    }
    return growth_type === 'lawn' ? '–' : 'Unbekannt';
  };

  const getSoilTypeDisplay = types =>
    translateEnumList(SOIL_TYPE_LABELS, types, 'soil_type', ', ') || 'Unbekannt';

  const getPhDisplay = () => {
    if (ph_min && ph_max) {
      return `pH ${ph_min} - ${ph_max}`;
    } else if (ph_min) {
      return `pH ab ${ph_min}`;
    } else if (ph_max) {
      return `pH bis ${ph_max}`;
    }
    return 'Nicht spezifiziert';
  };

  const getPlantTypeDisplay = value =>
    translateEnum(PLANT_TYPE_LABELS, value, 'plant_type') || 'Unbekannt';

  const getBloomMonthsDisplay = months => {
    if (!months) return 'Keine Angabe';

    // Check for string 'none' value (some plants may have this)
    if (months === 'none' || months === 'None') {
      return 'Keine';
    }

    // Handle array format
    if (Array.isArray(months)) {
      // Empty array [] means no flowering (e.g., Buchsbaum)
      if (months.length === 0) {
        return 'Keine';
      }
      // Array with single 0 also means no flowering
      if (months.length === 1 && months[0] === 0) {
        return 'Keine';
      }

      const monthNames = {
        1: 'Januar',
        2: 'Februar',
        3: 'März',
        4: 'April',
        5: 'Mai',
        6: 'Juni',
        7: 'Juli',
        8: 'August',
        9: 'September',
        10: 'Oktober',
        11: 'November',
        12: 'Dezember',
      };
      const displayMonths = months
        .map(m => monthNames[String(m)] || m)
        .filter(Boolean);

      if (displayMonths.length === 0) return 'Keine Angabe';
      if (displayMonths.length === 1) return displayMonths[0];
      if (displayMonths.length === 2) return displayMonths.join(' - ');
      return `${displayMonths[0]} - ${displayMonths[displayMonths.length - 1]}`;
    }

    // Handle string format (comma-separated)
    const monthArray = months.split(',').map(m => m.trim());
    const monthNames = {
      1: 'Januar',
      2: 'Februar',
      3: 'März',
      4: 'April',
      5: 'Mai',
      6: 'Juni',
      7: 'Juli',
      8: 'August',
      9: 'September',
      10: 'Oktober',
      11: 'November',
      12: 'Dezember',
    };
    const displayMonths = monthArray
      .map(m => monthNames[String(m)] || m)
      .filter(Boolean);

    if (displayMonths.length === 0) return 'Keine Angabe';
    if (displayMonths.length === 1) return displayMonths[0];
    if (displayMonths.length === 2) return displayMonths.join(' - ');
    return `${displayMonths[0]} - ${displayMonths[displayMonths.length - 1]}`;
  };

  const getFlowerColorDisplay = colors => {
    if (!colors) return null;
    const colorArray = Array.isArray(colors)
      ? colors
      : colors.split(',').map(c => c.trim());
    return colorArray.map(color => FLOWER_COLOR_MAP[color]).filter(Boolean);
  };

  // Helper function to parse and display space requirement
  const getSpaceRequirementDisplay = value => {
    if (!value) return null;
    // Parse "MIN-MAX" format (e.g., "30-50")
    const match = value.match(/^(\d+)-(\d+)$/);
    if (match) {
      return `${match[1]} - ${match[2]} cm`;
    }
    return value;
  };

  // Helper function to combine growth speed with rate
  const getCombinedGrowthSpeedDisplay = (speed, rate) => {
    const speedMap = {
      slow: 'Langsam',
      medium: 'Mittel',
      fast: 'Schnell',
    };
    const speedText = speedMap[speed] || speed;

    if (rate) {
      // Parse "MIN-MAX" format
      const match = rate.match(/^(\d+)-(\d+)$/);
      if (match) {
        return `${speedText} (${match[1]}-${match[2]} cm/Jahr)`;
      }
      return `${speedText} (${rate} cm/Jahr)`;
    }

    // Fallback to basic speed
    const basicMap = {
      slow: 'Langsam (<10 cm/Jahr)',
      medium: 'Mittel (10-30 cm/Jahr)',
      fast: 'Schnell (>30 cm/Jahr)',
    };
    return basicMap[speed] || speedText;
  };

  // Helper function to display root type
  const getRootTypeDisplay = value => {
    const rootMap = {
      shallow: 'Flachwurzler',
      deep: 'Tiefwurzler',
      heart: 'Herzwurzler',
    };
    return rootMap[value] || null;
  };

  // Helper function to display season (activity period)
  const getSeasonDisplay = (startMonth, endMonth) => {
    if (!startMonth || !endMonth) return null;

    const monthNames = {
      1: 'Januar',
      2: 'Februar',
      3: 'März',
      4: 'April',
      5: 'Mai',
      6: 'Juni',
      7: 'Juli',
      8: 'August',
      9: 'September',
      10: 'Oktober',
      11: 'November',
      12: 'Dezember',
    };

    const startName = monthNames[startMonth];
    const endName = monthNames[endMonth];

    if (!startName || !endName) return null;
    return `${startName} - ${endName}`;
  };

  // Helper function to display scent intensity
  const getScentIntensityDisplay = value => {
    const scentMap = {
      none: 'Kein Duft',
      light: 'Leicht',
      medium: 'Mittel',
      strong: 'Stark',
    };
    return scentMap[value] || null;
  };

  // Helper function to parse pH range string
  const parsePhRange = phRange => {
    if (!phRange) return null;
    // Parse "MIN-MAX" format (e.g., "5.5-7.0")
    const match = phRange.match(/^([\d.]+)-([\d.]+)$/);
    if (match) {
      return `pH ${match[1]} - ${match[2]}`;
    }
    return `pH ${phRange}`;
  };

  // Helper function to display bee_friendly as enum (not boolean!)
  const getBeeFriendlyDisplay = value => {
    // Handle both string enum and legacy boolean
    if (typeof value === 'string') {
      const beeMap = {
        none: 'Keine',
        low: 'Gering',
        medium: 'Mittel',
        high: 'Hoch',
      };
      return beeMap[value] || value;
    }
    // Legacy boolean fallback
    if (typeof value === 'boolean') {
      return value ? 'Ja' : 'Nein';
    }
    return null;
  };

  // Helper function to display climbing type
  const getClimbingTypeDisplay = value => {
    const climbingMap = {
      none: 'Keine Kletterpflanze',
      self_clinging: 'Selbstkletternd (Haftwurzeln)',
      twining: 'Schlingend (Triebe)',
      tendril: 'Rankend (Ranken)',
      scrambler: 'Spreizklimmer (Stacheln/Dornen)',
    };
    return climbingMap[value] || null;
  };

  // Helper function to get care priority badge
  const getCarePriorityBadge = priority => {
    if (!priority) return null;

    const priorityConfig = {
      low: { text: 'Niedrig', color: 'bg-green-100 text-green-800' },
      medium: { text: 'Mittel', color: 'bg-yellow-100 text-yellow-800' },
      high: { text: 'Hoch', color: 'bg-red-100 text-red-800' },
    };

    const config = priorityConfig[priority];
    if (!config) return null;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
      >
        {config.text}
      </span>
    );
  };

  // Helper function to display mulching info
  const getMulchingDisplay = (recommended, type, time) => {
    if (!recommended) return null;

    const mulchMap = {
      bark: 'Rinde',
      compost: 'Kompost',
      straw: 'Stroh',
      wood_chips: 'Holzhackschnitzel',
      leaves: 'Laub',
      gravel: 'Kies',
      none: null,
    };

    const timeMap = {
      spring: 'Frühjahr',
      late_spring: 'Spätes Frühjahr',
      autumn: 'Herbst',
      anytime: 'Jederzeit',
    };

    const mulchType = mulchMap[type] || type;
    const mulchTime = timeMap[time];

    if (mulchType && mulchTime) {
      return `${mulchType} - Beste Zeit: ${mulchTime}`;
    } else if (mulchType) {
      return mulchType;
    }
    return 'Empfohlen';
  };

  // Helper function to display repotting frequency
  const getRepottingDisplay = frequency => {
    const repotMap = {
      '1x_per_year': 'Jährlich',
      every_2_years: 'Alle 2 Jahre',
      when_rootbound: 'Bei Bedarf (wurzelgebunden)',
      rarely: 'Selten',
      none: null,
    };
    return repotMap[frequency] || null;
  };

  // Helper function to display propagation type
  const getPropagationDisplay = type => {
    const propagationMap = {
      seeds: 'Samen',
      cuttings: 'Stecklinge',
      division: 'Teilung',
      layering: 'Absenker',
      bulbs: 'Zwiebeln',
      runners: 'Ausläufer',
    };
    return propagationMap[type] || null;
  };

  // Helper function to get watering pattern display text
  const getWateringPatternDisplay = pattern => {
    const patternMap = {
      keep_evenly_moist:
        'Gleichmäßig feucht halten. Regelmäßig gießen, Boden sollte nie austrocknen.',
      dry_between_watering:
        'Zwischen dem Gießen abtrocknen lassen. Erst gießen wenn die obere Erdschicht trocken ist.',
      very_drought_tolerant:
        'Sehr trockenheitstolerant. Nur bei längerer Trockenheit gießen.',
      rain_only_established:
        'Nach Eingewöhnung nur bei Trockenheit. Etablierte Pflanzen kommen mit natürlichem Regen aus.',
      none: null,
    };
    return patternMap[pattern] || null;
  };

  // Helper function to get pruning display text
  const getPruningDisplay = (pruningType, bestTime) => {
    const timeMap = {
      early_spring: 'Frühjahr',
      late_spring: 'Spätes Frühjahr',
      late_winter: 'Spätwinter',
      after_flowering: 'Nach der Blüte',
      late_summer: 'Spätsommer',
      autumn: 'Herbst',
      anytime: 'Jederzeit möglich',
      anytime_if_needed: 'Bei Bedarf jederzeit',
    };

    const typeText = translateEnum(PRUNING_TYPE_LABELS, pruningType, 'pruning_type');
    const timeText = timeMap[bestTime];

    if (!typeText) return null;
    return timeText ? `${typeText} - ${timeText}` : typeText;
  };

  // Helper function to get fertilizer display text
  const getFertilizerDisplay = (fertilizerType, frequency) => {
    const typeMap = {
      organic: 'Organischer Dünger',
      liquid: 'Flüssigdünger',
      slow_release: 'Langzeitdünger',
      universal: 'Universaldünger',
      for_flowering_plants: 'Blühpflanzendünger',
      for_acid_loving: 'Rhododendrondünger',
      for_herbs: 'Kräuterdünger',
      for_hedges: 'Heckendünger',
      none: null,
    };
    const frequencyMap = {
      weekly: 'Wöchentlich',
      bi_weekly: 'Alle 2 Wochen',
      monthly_in_season: 'Monatlich in der Saison',
      '1x_per_year': 'Einmal jährlich',
      '2x_per_year': 'Zweimal jährlich',
      none: null,
    };

    const typeText = typeMap[fertilizerType];
    const freqText = frequencyMap[frequency];

    if (!typeText) return null;
    return freqText ? `${typeText} - ${freqText}` : typeText;
  };

  // Fallback generators for backend fields
  const generateFallbackWateringText = () => {
    const waterMap = {
      low: 'Wenig Wasser benötigt. Gießen Sie nur bei längerer Trockenheit (> 2 Wochen ohne Regen).',
      medium:
        'Mäßiger Wasserbedarf. Gießen Sie 1-2 mal pro Woche, abhängig von Witterung.',
      high: 'Hoher Wasserbedarf. Regelmäßig gießen, Boden sollte feucht bleiben.',
      very_high:
        'Sehr hoher Wasserbedarf. Täglich gießen oder konstant feuchten Boden gewährleisten.',
    };
    return (
      waterMap[water_requirement] ||
      'Wasserbedarf unbekannt. Bitte beobachten Sie die Pflanze regelmäßig.'
    );
  };

  const generateFallbackGeneralCareText = () => {
    return `Standort: ${getSunlightDisplay(
      sunlight
    )}. Wasserbedarf: ${getWaterDisplay(water_requirement)}. ${
      hardiness_zone ? `Winterhärte: ${hardiness_zone}.` : ''
    } Weitere Pflegehinweise folgen in Kürze.`;
  };

  // Extract care data from v7 JSONB structure
  const careData = {
    // Watering - prefer watering_pattern from care_instructions, fallback to generated text
    watering:
      getWateringPatternDisplay(care_instructions?.watering_pattern) ||
      generateFallbackWateringText(),
    // Pruning - combine pruning_type and pruning_best_time
    pruning: getPruningDisplay(
      care_instructions?.pruning_type,
      care_instructions?.pruning_best_time
    ),
    // Fertilizing - combine fertilizer_type and fertilizer_frequency
    fertilizing: getFertilizerDisplay(
      care_instructions?.fertilizer_type,
      care_instructions?.fertilizer_frequency
    ),
    // General care note
    general: care_instructions?.note_short || generateFallbackGeneralCareText(),
    // Winter protection info
    winterProtection: care_instructions?.winter_protection_needed
      ? care_instructions?.winter_protection_type
      : null,
    // Planting time
    plantingTime: care_instructions?.planting_time,
    // Harvest months
    harvestMonths: care_instructions?.harvest_months,
  };

  // Companion and antagonist plants from properties
  const companionPlantsArray = properties?.companion_plants || [];
  const antagonistPlantsArray = properties?.antagonist_plants || [];
  const companionPlantsText =
    companionPlantsArray.length > 0
      ? companionPlantsArray.join(', ')
      : 'Keine';
  const antagonistPlantsText =
    antagonistPlantsArray.length > 0 ? antagonistPlantsArray.join(', ') : null;

  // Additional notes from properties.additional_properties.notes.
  // Sanitized for brand voice (#470): the raw import text uses an ALL-CAPS
  // toxicity prefix + exclamation marks; toxicity itself is shown brand-clean
  // via ToxicWarningBadge/DataDisclaimerBadge above.
  const additionalNotesText = sanitizePlantNote(
    properties?.additional_properties?.notes
  );

  const isToxic =
    toxic_to_pets || toxic_to_children || toxicity_level !== 'none';

  return (
    /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- modal backdrop click-to-close */
    <div
      className={`plant-detail-dialog fixed inset-0 z-[60] flex items-center justify-center bg-black transition-opacity duration-200 ${
        isVisible ? 'bg-opacity-50' : 'bg-opacity-0'
      }`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="plant-detail-title"
    >
      <div
        ref={dialogRef}
        className={`bg-white rounded-2xl shadow-2xl flex flex-col transition-all duration-200 ${
          isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}
        style={{
          width: 'min(1188px, 100vw)',
          height: 'min(750px, 100dvh)',
          maxWidth: '100vw',
          maxHeight: '100dvh',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b border-gray-200"
          style={{ height: '60px' }}
        >
          <h2
            id="plant-detail-title"
            className="text-base font-bold text-gray-900"
          >
            {displayName}
            {scientific_name && (
              <span className="ml-2 text-sm font-normal italic text-gray-600">
                ({scientific_name})
              </span>
            )}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Dialog schließen"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Scrollable Body */}
        <div
          className="flex-1 overflow-y-auto px-6 py-6 custom-scrollbar"
          style={{ maxHeight: '630px' }}
        >
          {/* Large Plant Image */}
          <div className="flex justify-center mb-6">
            {imageName ? (
              <img
                src={getPlantImageUrl(imageName, 'detail')}
                alt={displayName || imageName}
                className="rounded-xl object-cover"
                style={{ width: '512px', height: '512px' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div
                className="bg-gray-100 rounded-xl flex items-center justify-center"
                style={{ width: '512px', height: '512px' }}
              >
                <Flower2 className="w-16 h-16 text-gray-400" />
              </div>
            )}
          </div>

          {/* Section 1: Grundinformationen */}
          <PlantDetailSection icon={Sprout} title="Grundinformationen">
            <div className="space-y-2">
              <div className="flex">
                <span className="w-48 text-sm font-medium text-gray-600">
                  Deutscher Name:
                </span>
                <span className="text-sm text-gray-900">
                  {displayName || 'Unbekannt'}
                </span>
              </div>
              <div className="flex">
                <span className="w-48 text-sm font-medium text-gray-600">
                  Wissenschaftlicher Name:
                </span>
                <span className="text-sm italic text-gray-900">
                  {scientific_name || 'Unbekannt'}
                </span>
              </div>
              {family && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Familie:
                  </span>
                  <span className="text-sm text-gray-900">{family}</span>
                </div>
              )}
              {plant_type && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Typ:
                  </span>
                  <span className="text-sm text-gray-900">
                    {getPlantTypeDisplay(plant_type)}
                  </span>
                </div>
              )}
            </div>
          </PlantDetailSection>

          {/* Section 2: Standort & Boden */}
          <PlantDetailSection icon={Sun} title="Standort & Boden">
            <div className="space-y-2">
              <div className="flex">
                <span className="w-48 text-sm font-medium text-gray-600">
                  Sonnenlicht:
                </span>
                <span className="text-sm text-gray-900">
                  {getSunlightDisplay(sunlight)}
                </span>
              </div>
              <div className="flex">
                <span className="w-48 text-sm font-medium text-gray-600">
                  Wasserbedarf:
                </span>
                <span className="text-sm text-gray-900">
                  {getWaterDisplay(water_requirement)}
                </span>
              </div>
              {soil_type && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Bodenbeschaffenheit:
                  </span>
                  <span className="text-sm text-gray-900">
                    {getSoilTypeDisplay(soil_type)}
                  </span>
                </div>
              )}
              <div className="flex">
                <span className="w-48 text-sm font-medium text-gray-600">
                  pH-Wert:
                </span>
                <span className="text-sm text-gray-900">
                  {parsePhRange(ph_range) || getPhDisplay()}
                </span>
              </div>
            </div>
          </PlantDetailSection>

          {/* Section 3: Wuchseigenschaften */}
          <PlantDetailSection icon={Ruler} title="Wuchseigenschaften">
            <div className="space-y-2">
              <div className="flex">
                <span className="w-48 text-sm font-medium text-gray-600">
                  Wuchshöhe:
                </span>
                <span className="text-sm text-gray-900">
                  {getHeightDisplay()}
                </span>
              </div>
              <div className="flex">
                <span className="w-48 text-sm font-medium text-gray-600">
                  Wuchsbreite:
                </span>
                <span className="text-sm text-gray-900">
                  {getWidthDisplay()}
                </span>
              </div>
              {growth_speed && (
                <div className="flex">
                  <div className="w-48 text-sm font-medium text-gray-600 flex items-center gap-2">
                    <span>Typische Wuchsgeschwindigkeit:</span>
                    <span className="relative group inline-flex">
                      <button
                        type="button"
                        className="p-0.5 text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded"
                        aria-describedby="growth-speed-tooltip"
                      >
                        <Info className="w-4 h-4" />
                      </button>
                      <div
                        id="growth-speed-tooltip"
                        role="tooltip"
                        className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 w-80 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-150 pointer-events-none"
                      >
                        <div className="bg-white/95 backdrop-blur-sm border-2 border-emerald-500 rounded shadow-lg px-3 py-2 text-xs text-gray-700 leading-snug whitespace-normal">
                          Diese Angabe ist nur als Indikator zu verstehen. Die tatsächlichen
                          Wuchsraten hängen von vielen Faktoren wie Standort, Boden,
                          Ernährung und Alter der Pflanzen ab.
                        </div>
                      </div>
                    </span>
                  </div>
                  <span className="text-sm text-gray-900">
                    {getCombinedGrowthSpeedDisplay(
                      growth_speed,
                      growth_rate_cm_per_year
                    )}
                  </span>
                </div>
              )}
              {space_requirement_cm && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Pflanzabstand:
                  </span>
                  <span className="text-sm text-gray-900">
                    {getSpaceRequirementDisplay(space_requirement_cm)}
                  </span>
                </div>
              )}
              {getRootTypeDisplay(root_type) && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Wurzeltyp:
                  </span>
                  <span className="text-sm text-gray-900">
                    {getRootTypeDisplay(root_type)}
                  </span>
                </div>
              )}
              {getSeasonDisplay(season_start_month, season_end_month) && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Aktivitätsphase:
                  </span>
                  <span className="text-sm text-gray-900">
                    {getSeasonDisplay(season_start_month, season_end_month)}
                  </span>
                </div>
              )}
            </div>
          </PlantDetailSection>

          {/* Section 4: Blüte & Ästhetik */}
          <PlantDetailSection icon={Flower2} title="Blüte & Ästhetik">
            <div className="space-y-3">
              {flowering_months !== undefined && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Blütezeit:
                  </span>
                  <span className="text-sm text-gray-900">
                    {getBloomMonthsDisplay(flowering_months)}
                  </span>
                </div>
              )}
              {flower_color && (
                <div className="flex items-start">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Blütenfarbe:
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {getFlowerColorDisplay(flower_color)?.map(
                      (color, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <div
                            className="w-5 h-5 rounded"
                            style={{
                              backgroundColor: color.hex,
                              border: color.border
                                ? `1px solid ${color.border}`
                                : '1px solid transparent',
                            }}
                            title={color.name}
                          />
                          <span className="text-sm text-gray-700">
                            {color.name}
                          </span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
              {evergreen !== undefined && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Immergrün:
                  </span>
                  <span className="text-sm text-gray-900">
                    {evergreen ? 'Ja' : 'Nein'}
                  </span>
                </div>
              )}
              {getScentIntensityDisplay(scent_intensity) && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Duftintensität:
                  </span>
                  <span className="text-sm text-gray-900">
                    {getScentIntensityDisplay(scent_intensity)}
                  </span>
                </div>
              )}
              {scent_type && scent_intensity && scent_intensity !== 'none' && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Duftart:
                  </span>
                  <span className="text-sm text-gray-900">{scent_type}</span>
                </div>
              )}
            </div>
          </PlantDetailSection>

          {/* Section 5: Sicherheit & Giftigkeit */}
          <PlantDetailSection
            icon={isToxic ? AlertTriangle : ShieldCheck}
            title="Sicherheit & Giftigkeit"
          >
            <div className="space-y-3">
              <ToxicWarningBadge
                isToxic={isToxic}
                toxicityLevel={toxicity_level}
                toxicToPets={toxic_to_pets}
                toxicToChildren={toxic_to_children}
                toxicToCats={toxic_to_cats}
                toxicToDogs={toxic_to_dogs}
                country={settings.country}
              />
              {isToxic && <DataDisclaimerBadge variant="toxicity" country={settings.country} />}
              {has_thorns && (
                <div className="flex items-center gap-2 mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                  <span className="text-sm text-yellow-800">
                    <strong>Achtung:</strong> Pflanze hat Dornen oder Stacheln
                  </span>
                </div>
              )}
            </div>
          </PlantDetailSection>

          {/* Section 6: Winterhärte */}
          <PlantDetailSection icon={Snowflake} title="Winterhärte">
            <div className="space-y-2">
              {care_instructions?.min_hardiness_zone && settings?.hardinessZone && (
                <div className="mb-3">
                  <HardinessWarningBadge
                    plantZone={care_instructions.min_hardiness_zone}
                    gardenZone={settings.hardinessZone}
                    variant="full"
                  />
                </div>
              )}
              {hardiness_zone && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Winterhärte-Zone:
                  </span>
                  <span className="text-sm text-gray-900">
                    {hardiness_zone}
                  </span>
                </div>
              )}
              {care_instructions?.min_hardiness_zone && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Minimale Winterhärtezone:
                  </span>
                  <span className="text-sm text-gray-900">
                    {care_instructions.min_hardiness_zone}
                  </span>
                </div>
              )}
              {frost_hardy !== undefined && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Frosthart:
                  </span>
                  <span className="text-sm text-gray-900">
                    {frost_hardy ? 'Ja' : 'Nein'}
                  </span>
                </div>
              )}
              {care_instructions?.frost_sensitive !== undefined && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Frostempfindlich:
                  </span>
                  <span className="text-sm text-gray-900">
                    {care_instructions.frost_sensitive ? 'Ja' : 'Nein'}
                  </span>
                </div>
              )}
              {care_instructions?.winter_protection_needed && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Winterschutz:
                  </span>
                  <span className="text-sm text-gray-900">
                    {care_instructions.winter_protection_type === 'fleece' &&
                      'Vlies'}
                    {care_instructions.winter_protection_type ===
                      'mulch_layer' && 'Mulchschicht'}
                    {care_instructions.winter_protection_type ===
                      'move_indoors' && 'Ins Haus holen'}
                    {care_instructions.winter_protection_type ===
                      'straw_cover' && 'Strohdecke'}
                    {!care_instructions.winter_protection_type && 'Benötigt'}
                  </span>
                </div>
              )}
              {care_instructions?.indoor_move_month && suitable_for_pot && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Ins Haus holen:
                  </span>
                  <span className="text-sm text-gray-900">
                    {
                      [
                        'Januar',
                        'Februar',
                        'März',
                        'April',
                        'Mai',
                        'Juni',
                        'Juli',
                        'August',
                        'September',
                        'Oktober',
                        'November',
                        'Dezember',
                      ][care_instructions.indoor_move_month - 1]
                    }
                  </span>
                </div>
              )}
            </div>
          </PlantDetailSection>

          {/* Section 7: Pflege */}
          <PlantDetailSection icon={Leaf} title="Pflege">
            <div className="space-y-3">
              {care_instructions?.priority && (
                <div className="flex items-center">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Pflegeaufwand:
                  </span>
                  {getCarePriorityBadge(care_instructions.priority)}
                </div>
              )}
              {drought_tolerant !== undefined && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Trockenheitstolerant:
                  </span>
                  <span className="text-sm text-gray-900">
                    {drought_tolerant ? 'Ja' : 'Nein'}
                  </span>
                </div>
              )}

              {/* Bewässerung */}
              <div className="flex">
                <span className="w-48 text-sm font-medium text-gray-600">
                  Bewässerung:
                </span>
                <span className="text-sm text-gray-900">
                  {careData.watering}
                </span>
              </div>

              {/* Schnitt */}
              {careData.pruning && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Schnitt:
                  </span>
                  <span className="text-sm text-gray-900">
                    {careData.pruning}
                    {care_instructions?.pruning_interval_years !== undefined &&
                      care_instructions.pruning_interval_years > 0 && (
                        <span className="text-gray-600 ml-2">
                          (Alle {care_instructions.pruning_interval_years} Jahr
                          {care_instructions.pruning_interval_years > 1
                            ? 'e'
                            : ''}
                          )
                        </span>
                      )}
                  </span>
                </div>
              )}

              {/* Düngung */}
              {careData.fertilizing && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Düngung:
                  </span>
                  <span className="text-sm text-gray-900">
                    {careData.fertilizing}
                  </span>
                </div>
              )}

              {/* Mulchen */}
              {getMulchingDisplay(
                care_instructions?.mulching_recommended,
                care_instructions?.mulch_type,
                care_instructions?.mulching_best_time
              ) && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Mulchen:
                  </span>
                  <span className="text-sm text-gray-900">
                    {getMulchingDisplay(
                      care_instructions.mulching_recommended,
                      care_instructions.mulch_type,
                      care_instructions.mulching_best_time
                    )}
                  </span>
                </div>
              )}

              {/* Umtopfen */}
              {suitable_for_pot &&
                getRepottingDisplay(care_instructions?.repotting_frequency) && (
                  <div className="flex">
                    <span className="w-48 text-sm font-medium text-gray-600">
                      Umtopfen:
                    </span>
                    <span className="text-sm text-gray-900">
                      {getRepottingDisplay(
                        care_instructions.repotting_frequency
                      )}
                    </span>
                  </div>
                )}

              {/* Vermehrung */}
              {getPropagationDisplay(care_instructions?.propagation_type) && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Vermehrung:
                  </span>
                  <span className="text-sm text-gray-900">
                    {getPropagationDisplay(care_instructions.propagation_type)}
                  </span>
                </div>
              )}

              {/* Pflanzzeit */}
              {care_instructions?.planting_time && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Pflanzzeit:
                  </span>
                  <span className="text-sm text-gray-900">
                    {care_instructions.planting_time === 'early_spring' &&
                      'Frühjahr'}
                    {care_instructions.planting_time === 'late_spring' &&
                      'Spätes Frühjahr'}
                    {care_instructions.planting_time === 'early_summer' &&
                      'Frühsommer'}
                    {care_instructions.planting_time === 'autumn' && 'Herbst'}
                    {care_instructions.planting_time === 'late_autumn' &&
                      'Spätherbst'}
                    {care_instructions.planting_time === 'anytime' &&
                      'Ganzjährig möglich'}
                  </span>
                </div>
              )}

              {/* Ernte */}
              {care_instructions?.harvest_months && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Erntezeit:
                  </span>
                  <span className="text-sm text-gray-900">
                    {(() => {
                      const match =
                        care_instructions.harvest_months.match(/^(\d+)-(\d+)$/);
                      if (match) {
                        const months = [
                          'Januar',
                          'Februar',
                          'März',
                          'April',
                          'Mai',
                          'Juni',
                          'Juli',
                          'August',
                          'September',
                          'Oktober',
                          'November',
                          'Dezember',
                        ];
                        return `${months[parseInt(match[1]) - 1]} - ${
                          months[parseInt(match[2]) - 1]
                        }`;
                      }
                      return care_instructions.harvest_months;
                    })()}
                  </span>
                </div>
              )}

              {/* Allgemeine Pflegehinweise */}
              <div className="flex">
                <span className="w-48 text-sm font-medium text-gray-600">
                  Allgemeine Hinweise:
                </span>
                <span className="text-sm text-gray-900">
                  {careData.general}
                </span>
              </div>
            </div>
          </PlantDetailSection>

          {/* Section 8: Kletterpflanzen (conditional) */}
          {is_climber && (
            <PlantDetailSection
              icon={Sprout}
              title="Kletterpflanzen-Eigenschaften"
            >
              <div className="space-y-2">
                {getClimbingTypeDisplay(climbing_type) && (
                  <div className="flex">
                    <span className="w-48 text-sm font-medium text-gray-600">
                      Kletterart:
                    </span>
                    <span className="text-sm text-gray-900">
                      {getClimbingTypeDisplay(climbing_type)}
                    </span>
                  </div>
                )}
                {needs_climbing_support !== undefined && (
                  <div className="flex">
                    <span className="w-48 text-sm font-medium text-gray-600">
                      Kletterhilfe benötigt:
                    </span>
                    <span className="text-sm text-gray-900">
                      {needs_climbing_support ? 'Ja' : 'Nein'}
                    </span>
                  </div>
                )}
                {invasive_to_masonry && (
                  <div className="flex items-center gap-2 mt-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                    <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <span className="text-sm text-red-800">
                      <strong>Warnung:</strong> Kann Mauerwerk beschädigen
                      (Haftwurzeln)
                    </span>
                  </div>
                )}
              </div>
            </PlantDetailSection>
          )}

          {/* Section 9: Ökologie & Besonderheiten */}
          <PlantDetailSection icon={Bug} title="Ökologie & Besonderheiten">
            <div className="space-y-2">
              {bee_friendly !== undefined &&
                getBeeFriendlyDisplay(bee_friendly) && (
                  <div className="flex">
                    <span className="w-48 text-sm font-medium text-gray-600">
                      Bienenfreundlichkeit:
                    </span>
                    <span className="text-sm text-gray-900">
                      {getBeeFriendlyDisplay(bee_friendly)}
                    </span>
                  </div>
                )}
              {suitable_for_pot !== undefined && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Kübel geeignet:
                  </span>
                  <span className="text-sm text-gray-900">
                    {suitable_for_pot ? 'Ja' : 'Nein'}
                  </span>
                </div>
              )}
              {suitable_for_planter !== undefined && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Balkonkasten:
                  </span>
                  <span className="text-sm text-gray-900">
                    {suitable_for_planter ? 'Ja' : 'Nein'}
                  </span>
                </div>
              )}
            </div>
          </PlantDetailSection>

          {/* Section 10: Begleitpflanzen & Verwendung */}
          <PlantDetailSection
            icon={Flower2}
            title="Begleitpflanzen & Verwendung"
          >
            <div className="space-y-2">
              <div className="flex">
                <span className="w-48 text-sm font-medium text-gray-600">
                  Gute Nachbarn:
                </span>
                <span className="text-sm text-gray-900">
                  {companionPlantsText}
                </span>
              </div>
              {antagonistPlantsText && (
                <div className="flex">
                  <span className="w-48 text-sm font-medium text-gray-600">
                    Schlechte Nachbarn:
                  </span>
                  <span className="text-sm text-gray-900">
                    {antagonistPlantsText}
                  </span>
                </div>
              )}
            </div>
          </PlantDetailSection>

          {/* Section 11: Zusätzliche Hinweise */}
          {(native_region ||
            deer_resistant !== undefined ||
            additionalNotesText) && (
            <PlantDetailSection icon={Info} title="Zusätzliche Hinweise">
              <div className="space-y-2">
                {native_region && (
                  <div className="flex">
                    <span className="w-48 text-sm font-medium text-gray-600">
                      Heimatregion:
                    </span>
                    <span className="text-sm text-gray-900">
                      {native_region}
                    </span>
                  </div>
                )}
                {deer_resistant !== undefined && (
                  <div className="flex">
                    <span className="w-48 text-sm font-medium text-gray-600">
                      Hirschresistent:
                    </span>
                    <span className="text-sm text-gray-900">
                      {deer_resistant ? 'Ja' : 'Nein'}
                    </span>
                  </div>
                )}
                {additionalNotesText && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {additionalNotesText}
                    </p>
                  </div>
                )}
              </div>
            </PlantDetailSection>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-center px-4 py-4 border-t border-gray-200 bg-gray-50"
          style={{ height: '60px' }}
        >
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Schließen
          </button>
        </div>
      </div>

      {/* Custom Scrollbar Styles */}
      <style>{`
        .plant-detail-dialog .w-48 {
          width: 16rem;
          white-space: nowrap;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #D1D5DB;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #9CA3AF;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #6B7280;
        }
      `}</style>
    </div>
  );
};

export default PlantDetailDialog;
